import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './guide-download-dialog.module.css';
import { PdfGuide, PDF_GUIDES } from '../../config/guide-downloads';

interface GuideDownloadDialogProps {
  open: boolean;
  onClose: () => void;
  /** Guides to display. Defaults to PDF_GUIDES from config */
  guides?: PdfGuide[];
}


async function triggerDownload(guide: PdfGuide): Promise<void> {
  if (!guide.downloadUrl) return;
  try {
    const response = await fetch(guide.downloadUrl, { mode: 'cors' });
    if (!response.ok) throw new Error('Download failed');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = guide.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    window.open(guide.downloadUrl, '_blank', 'noopener,noreferrer');
  }
}

export default function GuideDownloadDialog({ open, onClose, guides: guidesProp }: GuideDownloadDialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const guides = guidesProp ?? PDF_GUIDES;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleDownload = async (guide: PdfGuide) => {
    setDownloadingId(guide.id);
    try {
      await triggerDownload(guide);
      try {
        window.analytics?.trackEvent('guide_download', {
          guide_id: guide.id,
          guide_title: guide.title,
          guide_filename: guide.filename,
        });
      } catch { }
    } finally {
      setDownloadingId(null);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="guide-dialog-title"
    >
      <div className={styles.content} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose} aria-label="Close dialog">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className={styles.header}>
          <h2 id="guide-dialog-title" className={styles.title}>
            Thanks for subscribing!
          </h2>
          <p className={styles.subtitle}>
            Download the guides below to get started.
          </p>
        </div>

        <div className={styles.guideList}>
          {guides.map((guide) => (
            <div key={guide.id} className={styles.guideCard}>
              <h3 className={styles.guideTitle}>{guide.title}</h3>
              <p className={styles.guideDescription}>{guide.description}</p>
              <button
                className={styles.downloadButton}
                onClick={() => handleDownload(guide)}
                disabled={downloadingId === guide.id || !guide.downloadUrl}
              >
                <span className="material-icons-outlined" style={{ fontSize: '18px' }}>download</span>
                {downloadingId === guide.id ? 'Downloading...' : 'Download'}
              </button>
            </div>
          ))}
        </div>

        <div className={styles.footer}>
          <span className="material-icons-outlined" style={{ fontSize: '20px' }}>bookmark_add</span>
          <p>
            Bookmark this page and come back soon—we&rsquo;ll have more guides and science-backed health resources available for subscribers.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}
